import UseCasePage, {
  type UseCaseFaq,
  type UseCaseScene,
  type UseCaseStat,
  type UseCaseWhy,
} from "@/components/public/use-case-page"
import type { SeoMeta } from "@/types"

const WHY: UseCaseWhy[] = [
  {
    title: "Open houses live or die on bookings",
    description: "An open house that fills up creates bidding pressure and a story for the vendor. An empty one is just a Saturday wasted. A social video ad for the slot turns the Rightmove listing into a promotable event.",
  },
  {
    title: "Local reach without local spend",
    description: "Boosting the video in a 5-mile radius on Facebook or Instagram for \u00A320\u2013\u00A350 reaches more registered-interest buyers than most Rightmove alerts do in the same window.",
  },
  {
    title: "Builds anticipation in the days before",
    description: "Post the video 5\u20137 days before the open house, again 48 hours out, and one last nudge the morning of. Each impression is a reminder, not a fresh ad.",
  },
  {
    title: "Creates content after the event",
    description: "Filming snippets at the open house (outside only, respecting vendor privacy) gives you follow-up social content for the week after \u2014 proof points for the next vendor pitch.",
  },
]

const SCENES: UseCaseScene[] = [
  { title: "\"Open house\" event card", duration: "0:00\u20130:03", description: "Bold headline: \u201COPEN HOUSE SATURDAY\u201D with date and time stamp. Animated in over the hero photo. First 3 seconds earn the stop." },
  { title: "Property hero shot", duration: "0:03\u20130:08", description: "Best exterior frame with Ken Burns zoom. Address and price drop in as clean on-screen captions." },
  { title: "Three interior flashes", duration: "0:08\u20130:18", description: "Quick cuts of the showstopper rooms \u2014 kitchen, reception, master. The goal is hook, not full sell. One-line feature caption per cut." },
  { title: "Slot availability", duration: "0:18\u20130:24", description: "\u201CSlots every 15 min, 10am\u20132pm\u201D or similar. Positions the event as exclusive and limited, not drop-in casual." },
  { title: "Book-now CTA", duration: "0:24\u20130:30", description: "Phone, email, DM \u2014 with a reminder to book not just turn up. Voiceover closes: \u201Cregister interest now to secure your slot.\u201D" },
]

const STATS: UseCaseStat[] = [
  { value: "5\u00D7", label: "slots filled", detail: "A boosted 9:16 open house ad consistently outperforms a static social post for bookings." },
  { value: "3", label: "push points", detail: "7 days out, 48 hours out, morning of \u2014 one render, three posts, compounding impressions." },
  { value: "\u00A320\u201350", label: "boost budget", detail: "Typical local Meta boost needed to saturate a 5-mile radius for the week before." },
  { value: "\u221E", label: "slot variants", detail: "Re-render in seconds with a new date, time or slot count \u2014 no brief, no designer." },
]

const FAQ: UseCaseFaq[] = [
  {
    q: "When should I start promoting an open house?",
    a: "7 days out is ideal, with reminder posts 48 hours before and the morning of. Three touches beats one big push every time \u2014 the Instagram algorithm shows each impression to a slightly different audience slice.",
  },
  {
    q: "Should I boost the open house video on Meta?",
    a: "Yes, if you can spare \u00A320\u2013\u00A350. Target a 5-mile radius around the property, 28\u201360 age range, with an interest in \u201Creal estate\u201D or \u201Chome buyers.\u201D Even a small local boost dramatically increases the chance of filling the slots.",
  },
  {
    q: "What if the open house doesn't fill up?",
    a: "A half-full open house is still better than five scattered weekday viewings. The video also builds your brand in the area \u2014 future vendors in the radius see an active, local agent, which pays off on future instructions.",
  },
  {
    q: "Can the same video be re-used if the open house repeats?",
    a: "Yes. QuiteRight re-renders the video with a new date, time, or slot count in seconds. The creative engine is unchanged \u2014 only the date text and voiceover line are re-generated.",
  },
  {
    q: "Is this just for premium properties?",
    a: "No. Open houses work for anything \u00A3300k+ in a competitive area. They're especially effective on price-sensitive listings where bidding pressure from multiple simultaneous viewings can move the final offer.",
  },
]

export default function OpenHouseVideo({ meta }: { meta: SeoMeta }) {
  return (
    <UseCasePage
      meta={meta}
      useCaseName="Open House Video"
      breadcrumbName="Open House Video"
      heroHeadline="Fill every open house slot before Saturday even arrives"
      heroSubhead="A social video ad for an open house is the single most direct way to turn a Rightmove listing into a fully-booked event. QuiteRight renders the ad, adds the date, and publishes it to every channel \u2014 then re-publishes reminders as the event approaches."
      whyHeadline="Why the open house video outperforms a static social post"
      whySubhead="Open houses live or die on bookings. A video ad on Reels and Meta is the cheapest way to fill the room."
      whyItems={WHY}
      anatomyHeadline="Anatomy of an open house video"
      anatomySubhead="The QuiteRight open house template, configured with your date, time, and slot structure."
      scenes={SCENES}
      stats={STATS}
      faq={FAQ}
      ctaHeadline="Book every slot, every weekend"
      ctaBody="Turn every open house into a promotable event \u2014 branded, booked out, and fed straight to your socials in minutes."
      relatedKey="use-cases/open-house-video"
    />
  )
}
